import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Search, X } from "lucide-react";
import { ENDPOINTS, productPath, resolveAssetUrl } from "../config";
import { useCompare } from "../context/CompareContext";

function formatPrice(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `KSh ${n.toLocaleString()}`;
}

export default function Compare() {
    const { items, add, remove, clear, syncFamilies } = useCompare();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const boxRef = useRef(null);
    const abortRef = useRef(null);
    const reqIdRef = useRef(0);
    const debounceRef = useRef(null);

    const slugs = items.map((item) => item.slug).join(",");
    const family = items[0]?.family || [];
    const familyLabel = family.map((entry) => entry.name).filter(Boolean).join(", ");
    const familyParam = family.map((entry) => entry.id).filter(Boolean).join(",");

    useEffect(() => {
        if (!items.length) {
            setPayload(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        fetch(`${ENDPOINTS.COMPARE.PAIR}?slugs=${encodeURIComponent(slugs)}`)
            .then((res) => res.json())
            .then((body) => {
                if (cancelled) return;
                const data = body?.data || null;
                setPayload(data);
                if (data?.items?.length) syncFamilies(data.items);
            })
            .catch(() => {
                if (!cancelled) setPayload(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slugs, items.length, syncFamilies]);

    const fetchSuggestions = (raw) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = String(raw || "").trim();
        const addingSecond = Boolean(familyParam);
        const reqId = ++reqIdRef.current;
        if (!addingSecond && q.length < 2) {
            if (abortRef.current) abortRef.current.abort();
            abortRef.current = null;
            setSuggestions([]);
            setShowSuggestions(false);
            setSuggestLoading(false);
            setHasMore(false);
            return;
        }
        setShowSuggestions(true);
        setSuggestLoading(true);
        debounceRef.current = setTimeout(async () => {
            if (reqIdRef.current !== reqId) return;
            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            try {
                const params = new URLSearchParams({ limit: "10", skip: "0" });
                if (q.length >= 2) params.set("q", q);
                if (addingSecond) params.set("family", familyParam);
                const res = await fetch(`${ENDPOINTS.PRODUCTS.SUGGEST}?${params}`, {
                    signal: controller.signal,
                });
                const data = await res.json();
                if (reqIdRef.current !== reqId) return;
                const products = data?.data?.products || [];
                setSuggestions(Array.isArray(products) ? products : []);
                setHasMore(Boolean(data?.data?.hasMore));
                setShowSuggestions(true);
            } catch (err) {
                if (err.name === "AbortError") return;
                if (reqIdRef.current !== reqId) return;
                setSuggestions([]);
            } finally {
                if (reqIdRef.current === reqId) setSuggestLoading(false);
            }
        }, q.length >= 2 ? 250 : 0);
    };

    useEffect(() => {
        const onPointerDown = (event) => {
            if (boxRef.current && !boxRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, []);

    const columns = payload?.items?.length ? payload.items : items;
    const labels = useMemo(() => {
        if (!payload?.items?.length) return [];
        const seen = [];
        for (const item of payload.items) {
            for (const row of item.rows || []) {
                if (!seen.some((label) => label.toLowerCase() === row.label.toLowerCase())) {
                    seen.push(row.label);
                }
            }
        }
        return seen;
    }, [payload]);

    const valueFor = (item, label) => {
        const row = (item.rows || []).find((entry) => entry.label.toLowerCase() === label.toLowerCase());
        const raw = String(row?.value ?? "").trim();
        if (!row || !raw || raw === "—") return null;
        if (label.toLowerCase() === "price") return formatPrice(row.value);
        return raw;
    };

    const pick = async (product) => {
        const slug = product.variantSlug || product.slug;
        setShowSuggestions(false);
        setQuery("");
        setSuggestions([]);
        setHasMore(false);
        if (slug) await add(slug);
    };

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const params = new URLSearchParams({
                limit: "10",
                skip: String(suggestions.length),
            });
            const q = query.trim();
            if (q.length >= 2) params.set("q", q);
            if (familyParam) params.set("family", familyParam);
            const res = await fetch(`${ENDPOINTS.PRODUCTS.SUGGEST}?${params}`);
            const data = await res.json();
            const products = Array.isArray(data?.data?.products) ? data.data.products : [];
            setSuggestions((current) => {
                const seen = new Set(current.map((item) => item._id || item.slug));
                const extra = products.filter((item) => !seen.has(item._id || item.slug));
                return [...current, ...extra];
            });
            setHasMore(Boolean(data?.data?.hasMore));
        } catch {
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    };

    const slots = [columns[0] || null, columns[1] || null];

    return (
        <main className="pb-16">
            <Helmet>
                <title>Compare devices | FoneXpress</title>
            </Helmet>
            <div className="container mx-auto px-4 lg:px-8 py-8">
                <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Compare devices</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            {familyLabel
                                ? `Second device is limited to ${familyLabel}. The list opens in that category.`
                                : "First device can be anything. The second search stays in that category."}
                        </p>
                    </div>
                    {items.length > 0 && (
                        <button
                            type="button"
                            onClick={clear}
                            className="text-sm font-medium text-muted-foreground hover:text-primary"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                <div ref={boxRef} className="relative max-w-2xl mb-8">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/70 z-10" />
                    <input
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            fetchSuggestions(event.target.value);
                        }}
                        onFocus={() => {
                            if (familyParam && query.trim().length < 2) fetchSuggestions("");
                            else if (query.trim().length >= 2) setShowSuggestions(true);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && suggestions[0]) pick(suggestions[0]);
                        }}
                        placeholder={
                            familyLabel
                                ? `Search ${familyLabel} only…`
                                : "Search products, brands, categories…"
                        }
                        className="w-full bg-card border border-primary/20 rounded-full pl-11 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 shadow-sm"
                    />
                    {showSuggestions && (query.trim().length >= 2 || familyParam) && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-elevated z-50 max-h-[480px] overflow-y-auto">
                            {suggestLoading ? (
                                <div className="p-4 flex items-center justify-center text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Searching…
                                </div>
                            ) : suggestions.length > 0 ? (
                                suggestions.map((product, index) => (
                                    <button
                                        key={`${product._id || product.slug}-${index}`}
                                        type="button"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => pick(product)}
                                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface transition-colors border-b border-border/50 last:border-b-0 text-left"
                                    >
                                        <div className="w-10 h-10 shrink-0 product-image-well rounded-lg overflow-hidden flex items-center justify-center">
                                            {product.image ? (
                                                <img
                                                    src={resolveAssetUrl(product.image)}
                                                    alt=""
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            ) : (
                                                <Search className="h-4 w-4 text-muted-foreground" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-foreground font-medium line-clamp-2">{product.name}</div>
                                            {(product.categoryName || product.brandName) && (
                                                <div className="text-xs text-muted-foreground truncate">
                                                    {[product.categoryName, product.brandName].filter(Boolean).join(" · ")}
                                                </div>
                                            )}
                                        </div>
                                        {typeof product.price === "number" && (
                                            <span className="text-sm font-semibold text-primary whitespace-nowrap">
                                                KSh {Number(product.price).toLocaleString()}
                                            </span>
                                        )}
                                    </button>
                                ))
                            ) : (
                                <div className="p-4 text-center text-sm text-muted-foreground">No products found</div>
                            )}
                            {hasMore && suggestions.length > 0 && (
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    className="w-full text-center px-4 py-2.5 text-sm text-primary font-medium hover:bg-surface border-t border-border"
                                >
                                    {loadingMore ? "Loading…" : "Load more"}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {slots.map((item, index) => (
                        <div
                            key={item?.slug || `empty-${index}`}
                            className="rounded-2xl border border-border bg-card p-5 min-h-[220px] flex flex-col"
                        >
                            {item ? (
                                <>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="w-28 h-28 product-image-well rounded-xl flex items-center justify-center overflow-hidden">
                                            {item.image ? (
                                                <img
                                                    src={resolveAssetUrl(item.image)}
                                                    alt=""
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            ) : null}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => remove(item.slug)}
                                            className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10"
                                            aria-label={`Remove ${item.name}`}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <Link
                                        to={productPath(item.slug)}
                                        className="mt-4 font-semibold text-foreground hover:text-primary line-clamp-2"
                                    >
                                        {item.name}
                                    </Link>
                                    {item.brand ? (
                                        <p className="text-xs text-muted-foreground mt-1">{item.brand}</p>
                                    ) : null}
                                    {item.price != null && (
                                        <p className="mt-2 text-lg font-bold text-primary">{formatPrice(item.price)}</p>
                                    )}
                                </>
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center text-center text-muted-foreground border border-dashed border-border rounded-xl py-8">
                                    <Search className="h-5 w-5 mb-2 text-primary/70" />
                                    <p className="text-sm">
                                        {index === 0 ? "Search to add the first device" : "Search to add a second device"}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {loading && items.length > 0 && (
                    <p className="text-sm text-muted-foreground mb-4">Loading specifications…</p>
                )}

                {payload && payload.comparable === false && items.length === 2 && (
                    <p className="text-sm text-red-600 mb-4">{payload.reason}</p>
                )}

                {labels.length > 0 && !(items.length === 2 && payload?.comparable === false) && (
                    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
                        <table className="w-full text-sm">
                            <tbody>
                                {labels.map((label, rowIndex) => {
                                    const left = slots[0] ? valueFor(slots[0], label) : null;
                                    const right = slots[1] ? valueFor(slots[1], label) : null;
                                    const differs = Boolean(slots[0] && slots[1] && left !== right);
                                    const cell = (value) =>
                                        value ? (
                                            value
                                        ) : (
                                            <span className="text-xs font-normal text-muted-foreground/60">
                                                Not specified
                                            </span>
                                        );
                                    return (
                                        <tr key={label} className={rowIndex % 2 === 0 ? "bg-muted/30" : ""}>
                                            <th className="p-4 text-left font-medium text-muted-foreground w-36 sm:w-48 align-top">
                                                {label}
                                            </th>
                                            <td className={`p-4 align-top ${differs ? "bg-primary/10" : ""}`}>{cell(left)}</td>
                                            <td className={`p-4 align-top ${differs ? "bg-primary/10" : ""}`}>{cell(right)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </main>
    );
}
